// src/Views/Leistungen.js
import React, {useRef} from 'react';
import useReveal from '../hooks/useReveal';
import usePageMeta, {breadcrumbJsonLd} from '../seo/usePageMeta';
import {Link} from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import serviceIcons from './serviceIcons';
import './Pages.css';
import './Leistungen.css';

// Kurzfassung je Leistung. `short` ist die Einzeiler-Beschreibung für die
// kompakte Übersicht auf der Startseite, `desc` der Text auf der Leistungsseite.
const services = [
  {
    id: 'behandlungspflege',
    title: 'Behandlungspflege',
    short: 'Medikamente, Injektionen, Verbände',
    desc: 'Maßnahmen nach ärztlicher Verordnung: z.B. Medikamentengabe, Injektionen, Verbandswechsel.'
  },
  {
    id: 'grundpflege',
    title: 'Grundpflege',
    short: 'Körperpflege, Ernährung, Mobilität',
    desc: 'Unterstützung bei Körperpflege, Ernährung und Mobilität. Einfühlsam und zuverlässig.'
  },
  {
    id: 'rufbereitschaft',
    title: '24h-Rufbereitschaft',
    short: 'Rund um die Uhr erreichbar',
    desc: 'Rund um die Uhr erreichbar. Wir unterstützen, begleiten und aktivieren im Alltag.'
  },
  {
    id: 'beratung',
    title: 'Beratung',
    short: 'Anträge, Pflegegrad, Entlastung',
    desc: 'Individuelle Pflegeberatung: Anträge, Leistungen und Entlastungsangebote verständlich erklärt.'
  },
  {
    id: 'wundmanagement',
    title: 'Wundmanagement',
    short: 'Akute und chronische Wunden',
    desc: 'Moderne Wundversorgung durch geschultes Personal. Evidenzbasiert und sorgfältig dokumentiert.'
  },
  {
    id: 'palliativpflege',
    title: 'Palliativpflege',
    short: 'Würdevolle Begleitung zu Hause',
    desc: 'Würdevolle Begleitung, Symptomkontrolle und Entlastung für Angehörige. Zuhause und nah.'
  },
  {
    id: 'verhinderungspflege',
    title: 'Verhinderungspflege',
    short: 'Vertretung pflegender Angehöriger',
    desc: 'Vertretung pflegender Angehöriger. Geplant oder kurzfristig, zuverlässig und flexibel.'
  },
  {
    id: 'pflegeakademie',
    title: 'Pflegeakademie',
    short: 'Alltagsbegleiter nach § 45a SGB XI',
    desc: 'Qualifizierung zum Alltagsbegleiter nach § 45a SGB XI mit Basiskurs, Refresher und persönlicher fachlicher Begleitung.'
  }
];

const leistungenBreadcrumb = breadcrumbJsonLd([
  {name: 'Startseite', path: '/'},
  {name: 'Leistungen', path: '/leistungen'}
]);

const Leistungen = () => {
  const gridRef = useRef(null);

  useReveal(gridRef, '.srv-card', 'reveal');

  usePageMeta({
    title: 'Leistungen der ambulanten Pflege in Eitorf | Cura Care',
    description: 'Behandlungspflege, Grundpflege, Wundmanagement, Palliativpflege, Verhinderungspflege, Pflegeberatung und Pflegeakademie: alle Leistungen von Cura Care in Eitorf im Überblick.',
    path: '/leistungen',
    jsonLd: leistungenBreadcrumb
  });

  return (
    <div className="page-view">
      <Header />

      <main className="page leistungen-page" id="leistungen-page">
        <div className="page__inner">
          <nav className="breadcrumb" aria-label="Brotkrumen">
            <Link to="/">Startseite</Link>
            <span aria-hidden="true">/</span>
            <span>Leistungen</span>
          </nav>

          <div className="page__header">
            <h1>Leistungen</h1>
            <p>
              Unser Leistungsspektrum deckt alle Bereiche der ambulanten Pflege ab. Individuell geplant,
              zuverlässig umgesetzt und transparent begleitet.
            </p>
          </div>

          <section className="leistungen-section" aria-labelledby="leistungen-overview">
            <div className="leistungen-intro">
              <h2 id="leistungen-overview">Unsere Leistungen im Überblick</h2>
              <p>
                Alle Leistungen sind kombinierbar und werden auf Ihre Situation abgestimmt. Über das Menü
                gelangen Sie jederzeit zu den einzelnen Schwerpunkten.
              </p>
            </div>

            <div className="srv-grid" ref={gridRef}>
              {services.map((service) => (
                <Link
                  className="srv-card"
                  to={`/leistungen/${service.id}`}
                  key={service.id}
                  id={service.id}
                  aria-labelledby={`${service.id}-title`}
                >
                  <div className="srv-icon" aria-hidden="true">
                    {serviceIcons[service.id]}
                  </div>
                  <h3 className="srv-name" id={`${service.id}-title`}>{service.title}</h3>
                  <p className="srv-desc">{service.desc}</p>
                  <span className="srv-link">Mehr erfahren</span>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Leistungen;
export {services};
